import LgpdNotice from '@/components/feature/LgpdNotice';
import { useEffect, useState } from 'react';
import { depoimentosPalestra } from '../../mocks/depoimentos';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingCTA from '@/components/feature/FloatingCTA';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import AnimatedStatCard from '@/components/feature/AnimatedStatCard';

declare const gtag: (command: string, action: string, params?: Record<string, unknown>) => void;

// ─── Quote Form ───────────────────────────────────────────────────────────────

function QuoteForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    empresa: '',
    eventType: '',
    participants: '',
    date: '',
    local: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus('idle');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', empresa: '', eventType: '', participants: '', date: '', local: '', message: '' });
        if (typeof gtag !== 'undefined') {
          gtag('event', 'generate_lead', { event_category: 'Form', event_label: 'BH Quote' });
        }
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setStatus('idle'), 8000);
    }
  };

  const inputClass = 'w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none font-body text-sm text-gray-800 bg-white transition-colors';
  const labelClass = 'block font-body text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="bh-quote-form"
      aria-label="Formulário de orçamento palestra silenciosa Belo Horizonte"
      className="space-y-5"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="bh-name" className={labelClass}>Nome Completo *</label>
          <input type="text" id="bh-name" name="name" required value={formData.name} onChange={handleChange} placeholder="Seu nome" className={inputClass} />
        </div>
        <div>
          <label htmlFor="bh-email" className={labelClass}>E-mail *</label>
          <input type="email" id="bh-email" name="email" required value={formData.email} onChange={handleChange} placeholder="seu@empresa.com.br" className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="bh-phone" className={labelClass}>Telefone / WhatsApp *</label>
          <input type="tel" id="bh-phone" name="phone" required value={formData.phone} onChange={handleChange} placeholder="(31) 9 9999-9999" className={inputClass} />
        </div>
        <div>
          <label htmlFor="bh-empresa" className={labelClass}>Empresa / Instituição</label>
          <input type="text" id="bh-empresa" name="empresa" value={formData.empresa} onChange={handleChange} placeholder="Nome da empresa ou instituição" className={inputClass} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="bh-eventType" className={labelClass}>Tipo de Evento *</label>
          <select id="bh-eventType" name="eventType" required value={formData.eventType} onChange={handleChange} className={inputClass}>
            <option value="">Selecione...</option>
            <option value="Congresso Médico / Científico">Congresso Médico / Científico</option>
            <option value="Evento Corporativo">Evento Corporativo</option>
            <option value="Feira e Exposição">Feira e Exposição</option>
            <option value="Conferência Multilíngue">Conferência Multilíngue</option>
            <option value="Treinamento Corporativo">Treinamento Corporativo</option>
            <option value="Summit / Fórum">Summit / Fórum</option>
            <option value="Workshop / Capacitação">Workshop / Capacitação</option>
            <option value="Evento Cultural">Evento Cultural</option>
            <option value="Outro">Outro</option>
          </select>
        </div>
        <div>
          <label htmlFor="bh-participants" className={labelClass}>Número de Participantes</label>
          <select id="bh-participants" name="participants" value={formData.participants} onChange={handleChange} className={inputClass}>
            <option value="">Selecione...</option>
            <option value="Até 50">Até 50</option>
            <option value="50 a 150">50 a 150</option>
            <option value="150 a 300">150 a 300</option>
            <option value="300 a 500">300 a 500</option>
            <option value="500 a 2.000">500 a 2.000</option>
            <option value="Acima de 2.000">Acima de 2.000</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="bh-date" className={labelClass}>Data do Evento</label>
          <input type="date" id="bh-date" name="date" value={formData.date} onChange={handleChange} className={inputClass} />
        </div>
        <div>
          <label htmlFor="bh-local" className={labelClass}>Local / Venue</label>
          <input type="text" id="bh-local" name="local" value={formData.local} onChange={handleChange} placeholder="Ex.: Expominas, Minascentro..." className={inputClass} />
        </div>
      </div>

      <div>
        <label htmlFor="bh-message" className={labelClass}>Detalhes do Evento</label>
        <textarea id="bh-message" name="message" value={formData.message} onChange={handleChange} maxLength={500} rows={4} placeholder="Descreva o evento, número de trilhas simultâneas, idiomas necessários..." className={`${inputClass} resize-none`} />
        <p className="text-xs text-gray-400 mt-1">{formData.message.length}/500</p>
      </div>

      <LgpdNotice kind="orcamento" className="mb-4 col-span-full" />
            <button type="submit" disabled={isSubmitting} className="w-full bg-primary text-white py-4 rounded-full font-body font-bold text-base uppercase tracking-wide hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed">
        {isSubmitting ? 'ENVIANDO...' : 'SOLICITAR ORÇAMENTO GRATUITO'}
      </button>

      {status === 'success' && (
        <p className="text-center text-green-600 font-body text-sm font-semibold" role="status">
          Orçamento enviado! Nossa equipe retorna em até 24 horas.
        </p>
      )}
      {status === 'error' && (
        <p className="text-center text-red-500 font-body text-sm" role="alert">
          Erro ao enviar. Por favor, tente novamente ou ligue: (31) 9 9999-0000
        </p>
      )}
    </form>
  );
}

// ─── FAQ Item ─────────────────────────────────────────────────────────────────

function FAQItem({ question, answer, index }: { question: string; answer: string; index: number }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 rounded-2xl overflow-hidden" itemScope itemType="https://schema.org/Question">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-7 py-5 bg-white hover:bg-gray-50 transition-colors cursor-pointer text-left"
        aria-expanded={open}
        id={`faq-bh-${index}`}
      >
        <h4 className="font-body font-semibold text-secondary text-base leading-snug pr-4" itemProp="name">{question}</h4>
        <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-primary/10">
          <i className={`ri-${open ? 'subtract' : 'add'}-line text-primary text-base`}></i>
        </div>
      </button>
      {open && (
        <div className="px-7 pb-6 bg-white" itemScope itemType="https://schema.org/Answer" itemProp="acceptedAnswer">
          <p className="font-body text-gray-600 text-sm leading-relaxed" itemProp="text">{answer}</p>
        </div>
      )}
    </div>
  );
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const TESTIMONIALS = depoimentosPalestra.slice(0, 3).map((d) => ({
  name: d.nome,
  text: d.texto,
  context: d.contexto,
  rating: 5,
}));

const FAQS = [
  {
    question: 'Como funciona a palestra silenciosa no Expominas ou Minascentro em BH?',
    answer: 'No Expominas e Minascentro, nossa equipe instala transmissores de RF em pontos estratégicos para garantir cobertura total dos pavilhões. Os participantes recebem fones sem fio com até 42 canais de conteúdo simultâneo. O setup é feito com 2–3h de antecedência e inclui testes de sinal em toda a área do evento.',
  },
  {
    question: 'Quantas trilhas simultâneas são possíveis em Belo Horizonte?',
    answer: 'Nosso sistema suporta até 10 canais de conteúdo simultâneo em BH, ideal para congressos médicos, feiras corporativas e eventos com múltiplas sessões paralelas. Para grandes eventos como congressos da AMB ou fóruns da FIEMG, essa tecnologia elimina a necessidade de salas separadas, gerando economia de 40–60% nos custos de locação.',
  },
  {
    question: 'Vocês atendem congressos médicos em Belo Horizonte?',
    answer: 'Sim — congressos médicos e científicos em BH são uma das nossas especialidades. Já atendemos Sociedade Mineira de Cardiologia, CRM-MG, CFM eventos regionais e especialidades médicas no Expominas e Centro de Convenções. Entregamos toda documentação para prestação de contas e emitimos NF-e.',
  },
  {
    question: 'Qual a área de cobertura em BH e Grande BH?',
    answer: 'Atendemos toda a Região Metropolitana de Belo Horizonte: Savassi, Lourdes, Centro, Pampulha, Contagem, Betim, Nova Lima, Santa Luzia e toda a Grande BH. Para o Expominas (maior centro de convenções de MG), temos operação especializada com equipamentos de longa distância para cobrir os grandes pavilhões.',
  },
  {
    question: 'Qual o prazo mínimo para contratar palestra silenciosa em BH?',
    answer: 'Recomendamos contato com pelo menos 15 dias de antecedência para eventos de médio e grande porte. Para eventos até 200 participantes, conseguimos atender com 7 dias. Em casos emergenciais, avaliamos a disponibilidade de equipamentos — entre em contato e verificamos juntos.',
  },
  {
    question: 'Vocês emitem nota fiscal e documentação para prestação de contas em BH?',
    answer: 'Sim. Emitimos nota fiscal eletrônica, contrato formal e relatório técnico de uso. Ideal para congressos médicos e eventos que precisam de comprovação de despesas para associações, conselhos regionais e patrocinadores. Toda documentação é enviada em até 5 dias úteis após o evento.',
  },
];

const VENUES = [
  { name: 'Expominas', icon: 'ri-building-4-line', capacity: '10.000+' },
  { name: 'Minascentro', icon: 'ri-building-2-line', capacity: '3.000' },
  { name: 'Minas Tênis Clube', icon: 'ri-trophy-line', capacity: '2.000' },
  { name: 'Hotel Mercure Lourdes', icon: 'ri-hotel-line', capacity: '800' },
  { name: 'Ouro Minas Palace', icon: 'ri-hotel-line', capacity: '1.200' },
  { name: 'Auditorium BH', icon: 'ri-mic-line', capacity: '600' },
  { name: 'FIEMG — Centro Corporativo', icon: 'ri-building-line', capacity: '500' },
  { name: 'PUC Minas — Auditório', icon: 'ri-graduation-cap-line', capacity: '400' },
];

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function BeloHorizonteLandingPage() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: statsRef, isVisible: statsVisible } = useScrollAnimation({ threshold: 0.2 });
  const { ref: formRef, isVisible: formVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: benefitsRef, isVisible: benefitsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: venuesRef, isVisible: venuesVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: testimonialsRef, isVisible: testimonialsVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation({ threshold: 0.1 });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://stibpalestrasilenciosasp.com.br';

  useEffect(() => {
    document.title = 'Palestra Silenciosa Belo Horizonte | Congressos Médicos e Eventos BH | STIB';

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Palestra silenciosa Belo Horizonte: tecnologia premium para congressos médicos, feiras e eventos corporativos no Expominas e Minascentro. Até 4 trilhas simultâneas. Orçamento gratuito em 24h!'
      );
    }

    const geoPos = document.querySelector('meta[name="geo.position"]');
    if (geoPos) geoPos.setAttribute('content', '-19.9167;-43.9345');
    const geoReg = document.querySelector('meta[name="geo.region"]');
    if (geoReg) geoReg.setAttribute('content', 'BR-MG');
    const geoPlace = document.querySelector('meta[name="geo.placename"]');
    if (geoPlace) geoPlace.setAttribute('content', 'Belo Horizonte');

    const schemas = [
      {
        id: 'bh-localbusiness',
        data: {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'STIB Palestra Silenciosa Belo Horizonte',
          description: 'Aluguel de fones sem fio e sistemas de palestra silenciosa para congressos médicos, feiras e eventos corporativos em Belo Horizonte — MG.',
          url: `${siteUrl}/belo-horizonte`,
          telephone: '+55-11-91777-6227',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'São Paulo',
            addressRegion: 'SP',
            addressCountry: 'BR',
          },
          geo: { '@type': 'GeoCoordinates', latitude: -23.5505, longitude: -46.6333 },
          areaServed: { '@type': 'City', name: 'Belo Horizonte' },
        },
      },
      {
        id: 'bh-breadcrumb',
        data: {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
            { '@type': 'ListItem', position: 2, name: 'Palestra Silenciosa Belo Horizonte', item: `${siteUrl}/belo-horizonte` },
          ],
        },
      },
      {
        id: 'bh-faq',
        data: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQS.map((f) => ({
            '@type': 'Question',
            name: f.question,
            acceptedAnswer: { '@type': 'Answer', text: f.answer },
          })),
        },
      },
    ];

    schemas.forEach(({ id, data }) => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const s = document.createElement('script');
      s.id = id;
      s.type = 'application/ld+json';
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    });

    return () => {
      schemas.forEach(({ id }) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [siteUrl]);

  return (
    <>
      <Header />
      <FloatingCTA />

      {/* ── HERO ──────────────────────────────────────────────────────────── */}
      <section
        ref={heroRef}
        className="relative min-h-screen w-full flex items-center justify-center overflow-hidden"
        aria-label="Palestra Silenciosa Belo Horizonte — Hero"
      >
        <div className="absolute inset-0">
          <img
            src="/img/stib/cidade-belo-horizonte.webp"
            alt="Palestra Silenciosa Belo Horizonte — Congressos, feiras e eventos corporativos em MG"
            title="Palestra Silenciosa Belo Horizonte — STIB"
            className={`w-full h-full object-cover object-top transition-all duration-[1200ms] ${heroVisible ? 'scale-100 opacity-100' : 'scale-105 opacity-0'}`}
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/40 to-black/70"></div>
        </div>

        <div className="absolute top-24 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6">
            <Breadcrumb
              variant="light"
              items={[
                { label: 'Home', to: '/' },
                { label: 'Cidades', to: '/cidades' },
                { label: 'Palestra Silenciosa Belo Horizonte' },
              ]}
            />
          </div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto w-full">
          <div
            className={`inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold uppercase tracking-widest px-5 py-2 rounded-full mb-8 transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-primary text-sm"></i>
            </div>
            Belo Horizonte — Minas Gerais
          </div>

          <header>
            <h1
              className={`font-heading font-extrabold text-6xl md:text-8xl text-white mb-6 tracking-tight leading-none lowercase transition-all duration-700 delay-100 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              palestra silenciosa<br />
              <span className="text-primary">belo horizonte</span>
            </h1>
          </header>

          <p
            className={`font-body font-light text-xl md:text-2xl text-white/85 mb-10 max-w-3xl mx-auto leading-relaxed transition-all duration-700 delay-200 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            Tecnologia de áudio sem fio para congressos médicos, feiras no Expominas e eventos
            corporativos em BH. Até 4 trilhas simultâneas no mesmo espaço.
          </p>

          <div
            className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700 delay-300 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <a
              href="/orcamento"
              className="inline-flex items-center gap-3 bg-primary text-white px-10 py-4 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              SOLICITAR ORÇAMENTO
              <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                <i className="ri-arrow-down-line text-white text-lg"></i>
              </div>
            </a>
            <a
              href="https://wa.me/5531999990000?text=Olá! Preciso de orçamento para palestra silenciosa em Belo Horizonte."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/30 text-white px-10 py-4 rounded-full font-body font-semibold text-base hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-green-400 text-xl"></i>
              </div>
              WhatsApp BH
            </a>
          </div>

          <div
            className={`flex flex-wrap items-center justify-center gap-6 mt-14 transition-all duration-700 delay-400 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {[
              { icon: 'ri-stethoscope-line', label: 'Especialistas em congressos médicos' },
              { icon: 'ri-building-4-line', label: 'Expominas e Minascentro' },
              { icon: 'ri-timer-line', label: 'Orçamento em 24h' },
            ].map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-white/80 text-sm font-body">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={`${b.icon} text-primary text-base`}></i>
                </div>
                {b.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────────────────────── */}
      <section ref={statsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {[
              { numericValue: 60, suffix: '+', label: 'Empresas Atendidas', icon: 'ri-building-line' },
              { numericValue: 3, suffix: '', label: 'Idiomas Simultâneos', icon: 'ri-translate-2' },
              { numericValue: 4, suffix: '', label: 'Trilhas Simultâneas', icon: 'ri-broadcast-line' },
            ].map((s, i) => (
              <AnimatedStatCard
                key={i}
                numericValue={s.numericValue}
                suffix={s.suffix}
                label={s.label}
                icon={s.icon}
                delay={i * 100}
                visible={statsVisible}
                numberColor="text-secondary"
                className="p-8 rounded-2xl bg-gradient-to-br from-primary/5 to-secondary/5"
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── FORM + INFO ───────────────────────────────────────────────────── */}
      <section
        id="orcamento"
        ref={formRef}
        className="py-24 bg-gradient-to-br from-gray-50 to-white"
        aria-label="Formulário de orçamento palestra silenciosa Belo Horizonte"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Info lateral */}
            <div className={`transition-all duration-700 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
                Orçamento Gratuito · Retorno em 24 Horas
              </span>
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase leading-tight">
                quanto custa palestra silenciosa em belo horizonte?
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-10">
                Preencha o formulário ao lado e receba uma proposta personalizada para seu evento
                em BH — com nota fiscal, contrato e suporte técnico incluídos.
              </p>

              <div className="space-y-6 mb-10">
                {[
                  {
                    icon: 'ri-stethoscope-line',
                    title: 'Especialistas em Congressos Médicos',
                    desc: 'Experiência em eventos da AMB, CFM, CRM-MG e especialidades. Entendemos o protocolo e as exigências técnicas de cada tipo de congresso em BH.',
                  },
                  {
                    icon: 'ri-building-4-line',
                    title: 'Expertise no Expominas e Minascentro',
                    desc: 'Operação recorrente nos maiores espaços de MG. Conhecemos cada detalhe logístico — de pavilhões a salas VIP — sem surpresas no dia do evento.',
                  },
                  {
                    icon: 'ri-broadcast-line',
                    title: 'Até 4 Trilhas Simultâneas',
                    desc: 'Diferencial exclusivo em BH: 4 sessões paralelas no mesmo espaço. Ideal para congressos com tracks por especialidade — sem aluguel de salas extras.',
                  },
                  {
                    icon: 'ri-tools-line',
                    title: 'Técnico Presente no Evento',
                    desc: 'Setup completo, testes de cobertura e suporte em tempo real durante todo o seu evento em Belo Horizonte.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-5">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                      <i className={`${item.icon} text-primary text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-secondary text-base mb-1">{item.title}</h3>
                      <p className="font-body text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-secondary/5 border border-secondary/10 rounded-2xl p-6">
                <p className="font-body text-sm text-gray-600 leading-relaxed">
                  <strong className="text-secondary">Atendimento em Belo Horizonte — 2026:</strong><br />
                  Até 4 trilhas simultâneas no mesmo espaço<br />
                  Nota fiscal para prestação de contas<br />
                  Técnico presencial incluso em todos os pacotes
                </p>
              </div>
            </div>

            {/* Formulário */}
            <div className={`bg-white rounded-3xl p-10 border border-gray-100 transition-all duration-700 delay-200 ${formVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary">
                  <i className="ri-send-plane-line text-white text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading font-bold text-xl text-secondary">Solicite seu Orçamento</h3>
                  <p className="font-body text-xs text-gray-400">Gratuito · Sem compromisso · Resposta em 24h</p>
                </div>
              </div>
              <QuoteForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFÍCIOS / DIFERENCIAIS BH ──────────────────────────────────── */}
      <section ref={benefitsRef} className="py-24 bg-secondary text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Por que escolher a STIB em BH
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl lowercase transition-all duration-700 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              feito para o mercado de eventos em minas
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-stethoscope-line',
                title: 'Hub de Congressos Médicos do Brasil',
                desc: 'BH é a capital nacional de congressos médicos. Atendemos CFM, AMB, CRM-MG e dezenas de especialidades com equipamentos certificados e NF-e para prestação de contas.',
              },
              {
                icon: 'ri-building-4-line',
                title: 'Operação Especializada no Expominas',
                desc: 'Com capacidade para 10.000+ participantes, o Expominas é nossa "casa" em BH. Cobertura de sinal em toda a área dos pavilhões, inclusive em zonas de interferência metálica.',
              },
              {
                icon: 'ri-broadcast-line',
                title: '4 Trilhas no Mesmo Espaço',
                desc: 'O único provedor em BH com capacidade para 4 trilhas de conteúdo simultâneo. Revolucione seu congresso — múltiplas especialidades no mesmo pavilhão, sem paredes divisórias.',
              },
              {
                icon: 'ri-translate-2',
                title: 'Tradução Simultânea em 3 Idiomas',
                desc: 'Perfeito para fóruns internacionais e congressos com palestrantes estrangeiros em BH. Inglês, espanhol, francês — sem cabines físicas, com custo 60% menor.',
              },
              {
                icon: 'ri-file-list-3-line',
                title: 'Documentação para Prestação de Contas',
                desc: 'NF-e, contrato formal e relatório técnico de uso. Ideal para congressos que precisam de comprovação de despesas para associações médicas e conselhos.',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Suporte Técnico Dedicado',
                desc: 'Técnico da STIB presente do setup ao encerramento em BH. Resposta imediata a qualquer intercorrência — seu congresso ou evento nunca para por causa de áudio.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className={`bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-all duration-500 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary/20 mb-6">
                  <i className={`${item.icon} text-primary text-2xl`}></i>
                </div>
                <h3 className="font-heading font-bold text-white text-lg mb-3">{item.title}</h3>
                <p className="font-body text-white/70 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ESPAÇOS / VENUES ──────────────────────────────────────────────── */}
      <section ref={venuesRef} className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className={`transition-all duration-700 ${venuesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">Onde já atuamos</span>
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-secondary mb-6 lowercase leading-tight">
                principais espaços em belo horizonte
              </h2>
              <p className="font-body text-lg text-gray-600 leading-relaxed mb-10">
                Já realizamos eventos de <strong>palestra silenciosa em Belo Horizonte</strong> nos principais
                centros de convenções, hotéis e instalações corporativas de MG. Conhecemos cada detalhe
                logístico desses espaços.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {VENUES.map((v, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-4 p-4 rounded-xl bg-gray-50 hover:bg-primary/5 transition-all duration-500 ${venuesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: `${i * 60}ms` }}
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                      <i className={`${v.icon} text-primary text-base`}></i>
                    </div>
                    <div>
                      <p className="font-body font-semibold text-secondary text-sm">{v.name}</p>
                      <p className="font-body text-xs text-gray-400">até {v.capacity} pessoas</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={`relative h-[520px] rounded-3xl overflow-hidden transition-all duration-700 delay-200 ${venuesVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <img
                src="/img/stib/setor-medico.webp"
                alt="Expominas e centros de convenção em Belo Horizonte — Palestra Silenciosa BH"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-secondary/60 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <p className="font-body text-white/80 text-sm mb-2">Atendimento completo em</p>
                <h3 className="font-heading font-bold text-white text-2xl">BH e toda Grande BH</h3>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── DEPOIMENTOS ───────────────────────────────────────────────────── */}
      <section
        ref={testimonialsRef}
        className="py-24 bg-gradient-to-br from-gray-50 to-white"
        aria-label="Depoimentos de clientes Belo Horizonte"
        itemScope
        itemType="https://schema.org/ItemList"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Quem já contratou em BH
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              o que dizem nossos clientes
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className={`bg-white rounded-2xl p-8 border border-gray-100 hover:border-primary/20 transition-all duration-700 hover:-translate-y-1 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <div className="flex items-center gap-1 mb-5">
                  {[...Array(t.rating)].map((_, j) => (
                    <i key={j} className="ri-star-fill text-yellow-400 text-base"></i>
                  ))}
                </div>
                <p className="font-body text-gray-600 text-sm leading-relaxed mb-6 italic">
                  "{t.text}"
                </p>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary/10 flex-shrink-0">
                    <span className="font-heading font-bold text-secondary text-lg">{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-heading font-bold text-secondary text-sm">{t.name}</p>
                    {t.context && (
                      <p className="font-body text-xs text-gray-500">{t.context}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section
        ref={faqRef}
        className="py-24 bg-white"
        aria-label="Perguntas frequentes palestra silenciosa Belo Horizonte"
        itemScope
        itemType="https://schema.org/FAQPage"
      >
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="font-body text-primary font-semibold text-xs uppercase tracking-widest mb-4 block">
              Dúvidas Frequentes
            </span>
            <h2
              className={`font-heading font-bold text-5xl md:text-6xl text-secondary lowercase transition-all duration-700 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              faq — belo horizonte &amp; grande bh
            </h2>
          </div>

          <div className={`space-y-4 transition-all duration-700 delay-200 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            {FAQS.map((f, i) => (
              <FAQItem key={i} question={f.question} answer={f.answer} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ─────────────────────────────────────────────────────── */}
      <section className="relative py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/img/stib/cidade-belo-horizonte.webp"
            alt="Solicite orçamento palestra silenciosa Belo Horizonte"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary/85 via-secondary/70 to-secondary/50"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <div className="max-w-2xl">
              <h2 className="font-heading font-bold text-5xl md:text-6xl text-white lowercase leading-tight mb-6">
                pronto para transformar<br />
                seu evento em belo horizonte?
              </h2>
              <p className="font-body text-lg text-white/80 leading-relaxed">
                <strong className="text-white">Agenda limitada para BH.</strong> Garanta sua data e
                receba orçamento completo em até 24 horas — com nota fiscal e suporte técnico incluídos.
              </p>
            </div>

            <div className="flex flex-col gap-4 flex-shrink-0">
              <a
                href="/orcamento"
                className="inline-flex items-center justify-center gap-3 bg-primary text-white px-12 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-opacity-90 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                VER FORMULÁRIO DE ORÇAMENTO
                <div className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full">
                  <i className="ri-arrow-up-line text-white text-lg"></i>
                </div>
              </a>
              <a
                href="https://wa.me/5531999990000?text=Olá! Preciso de orçamento para palestra silenciosa em Belo Horizonte."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-white text-secondary px-12 py-5 rounded-full font-body font-bold text-base uppercase hover:bg-gray-100 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-green-500 text-xl"></i>
                </div>
                FALAR NO WHATSAPP
              </a>
              <Link
                to="/blog"
                className="text-center font-body text-sm text-white/70 hover:text-white underline underline-offset-4 transition-colors cursor-pointer"
              >
                Ler nossos guias sobre Palestra Silenciosa em BH &rarr;
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}